// fe/src/modules/billing/PaymentModal.tsx
// Mock "pay for your plan" popup used by the ingestion gate and the billing page.
// Pays through POST /plan/pay and sets the add-user screen's paid flag.

import React, { useEffect, useState } from "react";
import { CheckCircle2, CreditCard, Loader2, Lock, X } from "lucide-react";
import { formatMoney, markUserSidePaid, payPlan, type PlanInvoice, type PlanResponse } from "./planApi";

export type PaymentContext = "ingest" | "users" | "billing";

interface PaymentModalProps {
    open: boolean;
    plan: PlanResponse;
    context?: PaymentContext;
    onClose: () => void;
    onPaid: (plan: PlanResponse) => void;
}

const CONTEXT_COPY: Record<PaymentContext, string> = {
    ingest: "Your plan needs to be paid before you can upload assets.",
    users: "Your plan needs to be paid before you can add more users.",
    billing: "Pay for your plan to start uploading assets and adding users.",
};

const PaymentModal: React.FC<PaymentModalProps> = ({ open, plan, context = "billing", onClose, onPaid }) => {
    const [holderName, setHolderName] = useState("");
    const [cardNumber, setCardNumber] = useState("4242 4242 4242 4242");
    const [expiry, setExpiry] = useState("12/28");
    const [cvc, setCvc] = useState("123");
    const [step, setStep] = useState<"form" | "processing" | "done">("form");
    const [error, setError] = useState<string | null>(null);
    const [invoice, setInvoice] = useState<PlanInvoice | null>(null);
    const [paidPlan, setPaidPlan] = useState<PlanResponse | null>(null);

    useEffect(() => {
        if (open) {
            setStep("form");
            setError(null);
            setInvoice(null);
            setPaidPlan(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && step !== "processing") handleClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });

    if (!open) return null;

    const price = plan.plan.price;
    const digits = cardNumber.replace(/\D/g, "");
    const canPay = holderName.trim().length > 1 && digits.length >= 12 && /^\d{2}\/\d{2}$/.test(expiry) && /^\d{3,4}$/.test(cvc);

    const handlePay = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canPay) return;
        setStep("processing");
        setError(null);
        try {
            // Short pause so the demo reads like a real card authorisation.
            await new Promise((r) => setTimeout(r, 1200));
            const result = await payPlan({ last4: digits.slice(-4), holderName: holderName.trim() });
            markUserSidePaid(result.plan);
            setInvoice(result.invoice ?? result.plan.invoices[0] ?? null);
            setPaidPlan(result.plan);
            setStep("done");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Payment failed");
            setStep("form");
        }
    };

    // Report success only when the popup is dismissed, so the success screen stays visible.
    const handleClose = () => {
        if (paidPlan) onPaid(paidPlan);
        onClose();
    };

    const input =
        "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="pay-title">
            <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl">
                <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
                    <div>
                        <h2 id="pay-title" className="text-lg font-semibold text-gray-900">
                            {step === "done" ? "Payment received" : "Pay for your plan"}
                        </h2>
                        {step !== "done" && <p className="mt-1 text-sm text-gray-500">{CONTEXT_COPY[context]}</p>}
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={step === "processing"}
                        className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                {step === "done" ? (
                    <div className="px-6 py-8 text-center">
                        <CheckCircle2 className="mx-auto text-green-600" size={48} />
                        <p className="mt-4 text-gray-900">
                            Your plan is active. You can now upload assets and add users.
                        </p>
                        {invoice && (
                            <p className="mt-2 text-sm text-gray-500">
                                Invoice {invoice.invoiceNumber} for {formatMoney(invoice.total, invoice.currency)} is in Financial &amp; Billing.
                            </p>
                        )}
                        <button onClick={handleClose} className="mt-6 rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700">
                            Done
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handlePay} className="px-6 py-5">
                        <div className="rounded-xl bg-gray-50 p-4">
                            <div className="flex items-baseline justify-between">
                                <span className="text-sm font-medium text-gray-900">
                                    {plan.plan.userLimit} users, {plan.plan.storageLimitLabel} storage
                                </span>
                                <span className="text-sm text-gray-500">Monthly</span>
                            </div>
                            <dl className="mt-3 space-y-1 text-sm">
                                {price.lineItems.map((li) => (
                                    <div key={li.code} className="flex justify-between text-gray-600">
                                        <dt>{li.description}</dt>
                                        <dd>{formatMoney(li.amount, price.currency)}</dd>
                                    </div>
                                ))}
                                {price.tax > 0 && (
                                    <div className="flex justify-between text-gray-600">
                                        <dt>Tax ({Math.round(price.taxRate * 100)}%)</dt>
                                        <dd>{formatMoney(price.tax, price.currency)}</dd>
                                    </div>
                                )}
                                <div className="flex justify-between border-t border-gray-200 pt-2 font-semibold text-gray-900">
                                    <dt>Due today</dt>
                                    <dd>{formatMoney(price.total, price.currency)}</dd>
                                </div>
                            </dl>
                        </div>

                        <div className="mt-5 space-y-3">
                            <label className="block">
                                <span className="text-sm text-gray-700">Name on card</span>
                                <input autoFocus value={holderName} onChange={(e) => setHolderName(e.target.value)} className={`${input} mt-1`} placeholder="Full name" />
                            </label>
                            <label className="block">
                                <span className="text-sm text-gray-700">Card number</span>
                                <div className="relative mt-1">
                                    <CreditCard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} className={`${input} pl-9`} inputMode="numeric" />
                                </div>
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="text-sm text-gray-700">Expiry (MM/YY)</span>
                                    <input value={expiry} onChange={(e) => setExpiry(e.target.value)} className={`${input} mt-1`} />
                                </label>
                                <label className="block">
                                    <span className="text-sm text-gray-700">CVC</span>
                                    <input value={cvc} onChange={(e) => setCvc(e.target.value)} className={`${input} mt-1`} inputMode="numeric" />
                                </label>
                            </div>
                        </div>

                        {error && <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

                        <button
                            type="submit"
                            disabled={!canPay || step === "processing"}
                            className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {step === "processing" ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" /> Processing payment
                                </>
                            ) : (
                                <>Pay {formatMoney(price.total, price.currency)}</>
                            )}
                        </button>
                        <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-500">
                            <Lock size={12} /> Demo payment. No card is charged.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};

export default PaymentModal;